/* External */
import {
  memo, useEffect, useState,
} from 'react';
import { IntlProvider } from 'react-intl';
import PropTypes from 'prop-types';

/* Utils */
import { Locale } from '../../utils/constants';
import { useIsBrazil } from '../ConfigProvider';
import platform from '../../utils/platform';

/* Only the active locale's messages are fetched (one JSON per locale, split
   into its own chunk); the boot entry no longer carries all four. Locale.EN
   is never selected at runtime (userLanguage is PT or ES), so en.json is
   dropped entirely. */
const messagesLoader = {
  [Locale.ES]: () => import('../../languages/messages/es.json'),
  [Locale.PT]: () => import('../../languages/messages/pt.json'),
  skymais: () => import('../../languages/messages/pt-skymais.json'),
};

const Theme = {
  DGO: 'dgo',
  SKYMAIS: 'skymais',
};

let theme = import.meta.env.VITE_THEME || Theme.DGO;
if (!Object.values(Theme).includes(theme)) [theme] = Theme;

const isSkyMais = theme === Theme.SKYMAIS;

function IntlProviderWrapper({ children }) {
  const isBrazil = useIsBrazil();
  const userLanguage = isBrazil || isSkyMais ? Locale.PT : Locale.ES;
  const [timeZone, setTimeZone] = useState();
  const [currentMessages, setCurrentMessages] = useState();

  useEffect(() => {
    async function getAndSetTimeZone() {
      const currentTimeZone = platform.isTizen ? await platform.getTimezone() || undefined
        : undefined;
      setTimeZone(currentTimeZone);
    }
    getAndSetTimeZone();
  }, []);

  useEffect(() => {
    let cancelled = false;
    /* theme is 'dgo' | 'skymais' but the loader map is keyed by locale —
       fall back to the resolved user locale exactly like the old static map. */
    const loader = messagesLoader[theme] || messagesLoader[userLanguage];
    const apply = (messages) => {
      if (!cancelled) setCurrentMessages(messages);
    };
    loader().then((messages) => apply(messages.default)).catch(() => apply({}));
    return () => { cancelled = true; };
  }, [userLanguage]);

  /* Render nothing until the locale chunk lands: the tree below never mounts
     and the boot loader (outside React) stays visible for the extra ~100 ms. */
  // eslint-disable-next-line unicorn/no-null -- tree below never mounts until messages are ready
  if (!currentMessages) return null;

  return (
    <IntlProvider
      locale={userLanguage}
      messages={currentMessages}
      timeZone={timeZone}
    >
      {children}
    </IntlProvider>
  );
}

IntlProviderWrapper.propTypes = {
  children: PropTypes.element.isRequired,
};

export default memo(IntlProviderWrapper);
